import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Loader2, Plus, Sparkles, Trash2, CheckSquare, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChecklistPage() {
  const { t } = useTranslation();
  const [checklists, setChecklists] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [genForm, setGenForm] = useState({ form_id: "", situation: "" });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [clRes, fRes] = await Promise.all([
        axios.get(`${API}/checklists`, { withCredentials: true }),
        axios.get(`${API}/forms`),
      ]);
      setChecklists(clRes.data);
      setForms(fRes.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleGenerate = async () => {
    if (!genForm.form_id) { toast.error(t("pages.selectFormFirst")); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/checklists/generate`, genForm, { withCredentials: true });
      setChecklists((prev) => [res.data, ...prev]);
      toast.success("Checklist generated!");
      setDialogOpen(false);
      setGenForm({ form_id: "", situation: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate checklist");
    }
    setGenerating(false);
  };

  const handleToggle = async (checklistId, itemIndex) => {
    try {
      const res = await axios.put(
        `${API}/checklists/${checklistId}/items/${itemIndex}`,
        {},
        { withCredentials: true }
      );
      setChecklists((prev) =>
        prev.map((cl) =>
          cl.checklist_id === checklistId ? { ...cl, items: res.data.items } : cl
        )
      );
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (checklistId) => {
    try {
      await axios.delete(`${API}/checklists/${checklistId}`, { withCredentials: true });
      setChecklists((prev) => prev.filter((cl) => cl.checklist_id !== checklistId));
      toast.success("Checklist deleted");
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="checklist-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="checklist-page">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{t("pages.checklistsTitle")}</h1>
            <p className="text-base text-slate-500">{t("pages.checklistsSubtitle")}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="generate-checklist-btn">
                <Plus className="mr-2 h-4 w-4" /> {t("pages.generate")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{t("pages.generateChecklist")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("pages.selectForm")}</Label>
                  <Select value={genForm.form_id} onValueChange={(v) => setGenForm((p) => ({ ...p, form_id: v }))}>
                    <SelectTrigger data-testid="checklist-form-select">
                      <SelectValue placeholder={t("pages.chooseForm")} />
                    </SelectTrigger>
                    <SelectContent>
                      {forms.map((f) => (
                        <SelectItem key={f.form_id} value={f.form_id}>{f.code} - {f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("pages.yourSituation")}</Label>
                  <Textarea
                    placeholder={t("pages.situationPlaceholder")}
                    value={genForm.situation}
                    onChange={(e) => setGenForm((p) => ({ ...p, situation: e.target.value }))}
                    data-testid="checklist-situation-input"
                  />
                </div>
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="w-full bg-teal-700 hover:bg-teal-800 text-white font-medium"
                  data-testid="checklist-generate-submit"
                >
                  {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  {t("pages.generateWithAI")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {checklists.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200" data-testid="checklist-empty">
            <CardContent className="p-12 text-center">
              <CheckSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">{t("pages.noChecklists")}</h3>
              <p className="text-sm text-slate-500 mb-4">{t("pages.noChecklistsDesc")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {checklists.map((cl) => {
              const done = cl.items?.filter((i) => i.completed).length || 0;
              const total = cl.items?.length || 0;
              return (
                <Card key={cl.checklist_id} className="border-slate-100" data-testid={`checklist-${cl.checklist_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-4 w-4 text-slate-500" />
                          <h3 className="text-base font-semibold text-slate-900">{cl.title}</h3>
                        </div>
                        <p className="text-sm text-slate-500">{done} {t("pages.ofTotal")} {total} {t("pages.completed")}</p>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(cl.checklist_id)}
                        className="text-slate-400 hover:text-red-500"
                        data-testid={`delete-checklist-${cl.checklist_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                      <div
                        className="h-full bg-teal-500 rounded-full transition-all duration-500"
                        style={{ width: total > 0 ? `${(done / total) * 100}%` : '0%' }}
                      />
                    </div>
                    <div className="space-y-2">
                      {cl.items?.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200"
                          data-testid={`checklist-item-${cl.checklist_id}-${idx}`}
                        >
                          <Checkbox
                            checked={item.completed}
                            onCheckedChange={() => handleToggle(cl.checklist_id, idx)}
                            className="mt-0.5"
                            data-testid={`checklist-checkbox-${cl.checklist_id}-${idx}`}
                          />
                          <div className="flex-1">
                            <span className={`text-sm ${item.completed ? "line-through text-slate-400" : "text-slate-700"}`}>
                              {item.text}
                            </span>
                          </div>
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              item.category === "required" ? "border-red-200 text-red-600" :
                              item.category === "recommended" ? "border-amber-200 text-amber-600" :
                              "border-slate-200 text-slate-500"
                            }`}
                          >
                            {item.category}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
